import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import {
  getSOVLines,
  createSOVLine,
  updateSOVLine,
  deleteSOVLine,
  checkSOVAllocation,
  rollupSOVLine,
  resetSOVProgress,
  getSOVChangeLog,
} from '../api';
import ConfirmDialog from './ConfirmDialog';
import useConfirm from '../hooks/useConfirm';
import { useTheme } from '../hooks/useTheme';

// Category color mapping matching Base44 design
const getCategoryStyle = (category) => {
  switch ((category || '').toLowerCase()) {
    case 'material':
      return { backgroundColor: '#FFF3E0', color: '#E65100', border: '1px solid #FFB74D' };
    case 'labor':
      return { backgroundColor: '#E3F2FD', color: '#1565C0', border: '1px solid #64B5F6' };
    case 'general conditions':
      return { backgroundColor: '#F5F5F5', color: '#616161', border: '1px solid #BDBDBD' };
    case 'equipment':
      return { backgroundColor: '#E8F5E9', color: '#2E7D32', border: '1px solid #81C784' };
    case 'other':
      return { backgroundColor: '#F3E5F5', color: '#6A1B9A', border: '1px solid #BA68C8' };
    default:
      return { backgroundColor: '#F5F5F5', color: '#616161', border: '1px solid #BDBDBD' };
  }
};

const SOVTab = ({ projectId, refreshSummary }) => {
  const t = useTheme();
  const { confirm, confirmDialogProps } = useConfirm();
  const [sovLines, setSovLines] = useState([]);
  const [allocationStatus, setAllocationStatus] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    sov_code: '',
    description: '',
    building_area_phase: '',
    billing_category: 'Labor',
    contract_amount: '',
    billing_method: 'Percent Complete',
    retainage_percent: 0,
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // SOV Properties modal
  const [propsModal, setPropsModal] = useState(null); // SOV line object or null
  const [propsForm, setPropsForm] = useState({});
  const [propsChangeLog, setPropsChangeLog] = useState([]);
  const [propsSaving, setPropsSaving] = useState(false);
  const [propsChangeReason, setPropsChangeReason] = useState('');

  const openPropsModal = async (line) => {
    setPropsModal(line);
    setPropsForm({
      sov_code: line.sov_code || '',
      description: line.description || '',
      building_area_phase: line.building_area_phase || '',
      billing_category: line.billing_category || 'Labor',
      contract_amount: line.contract_amount || 0,
      retainage_percent: line.retainage_percent ?? 5,
      retainage_percent_stored: line.retainage_percent_stored ?? line.retainage_percent ?? 5,
      billing_method: line.billing_method || 'Percent Complete',
      notes: line.notes || '',
    });
    setPropsChangeReason('');
    try {
      const logs = await getSOVChangeLog(line.id);
      setPropsChangeLog(logs);
    } catch { setPropsChangeLog([]); }
  };

  const handlePropsSave = async () => {
    if (!propsModal) return;
    setPropsSaving(true);
    try {
      await updateSOVLine(propsModal.id, { ...propsForm, change_reason: propsChangeReason || null });
      setPropsModal(null);
      await fetchSOVLines();
      if (refreshSummary) refreshSummary();
    } catch (e) {
      alert(e.message || 'Failed to save');
    } finally { setPropsSaving(false); }
  };

  // Billed vs Projected toggle
  const [sovView, setSovView] = useState('projected'); // 'billed' | 'projected'

  // Resizable columns
  const COL_RATIOS = [55, 195, 80, 100, 85, 85, 85, 85, 45, 85, 75, 75, 40];
  const COL_RATIO_SUM = COL_RATIOS.reduce((a, b) => a + b, 0);
  const COL_NAMES = ['Item No.', 'Description', 'Category', 'Sched. Value', 'Previous', 'This Period', 'Stored Mat.', 'Total', '%', 'Balance', 'Retainage', 'Released', ''];
  const COL_MIN = 40;
  const tableContainerRef = useRef(null);
  const [colWidths, setColWidths] = useState(COL_RATIOS);
  const resizingCol = useRef(null);
  const resizeStartX = useRef(0);
  const resizeStartW = useRef(0);
  const [activeResizeCol, setActiveResizeCol] = useState(null);
  const hasAutoSized = useRef(false);

  // Scale columns proportionally to fill container width
  const scaleColumnsToFit = useCallback((containerWidth) => {
    if (!containerWidth || containerWidth < 400) return;
    const available = containerWidth - 2; // account for border
    return COL_RATIOS.map(r => Math.max(COL_MIN, Math.round((r / COL_RATIO_SUM) * available)));
  }, []);

  // Auto-fit columns to container on mount and window resize
  useEffect(() => {
    const fitColumns = () => {
      if (resizingCol.current !== null) return; // don't interfere with manual resize
      const el = tableContainerRef.current;
      if (!el) return;
      const width = el.clientWidth;
      const scaled = scaleColumnsToFit(width);
      if (scaled) setColWidths(scaled);
    };

    // Use ResizeObserver so it reacts to container size changes (not just window)
    const el = tableContainerRef.current;
    if (!el) return;

    const observer = new ResizeObserver(() => {
      if (!hasAutoSized.current) {
        fitColumns();
        hasAutoSized.current = true;
      }
    });
    observer.observe(el);

    // Also fit on window resize
    const handleWindowResize = () => {
      if (resizingCol.current !== null) return;
      fitColumns();
    };
    window.addEventListener('resize', handleWindowResize);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [scaleColumnsToFit]);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (resizingCol.current === null) return;
      const diff = e.clientX - resizeStartX.current;
      const newW = Math.max(COL_MIN, resizeStartW.current + diff);
      setColWidths(prev => {
        const next = [...prev];
        next[resizingCol.current] = newW;
        return next;
      });
    };
    const onMouseUp = () => {
      if (resizingCol.current !== null) {
        resizingCol.current = null;
        setActiveResizeCol(null);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const startColResize = useCallback((colIdx, e) => {
    e.preventDefault();
    resizingCol.current = colIdx;
    setActiveResizeCol(colIdx);
    resizeStartX.current = e.clientX;
    resizeStartW.current = colWidths[colIdx];
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [colWidths]);

  // Double-click resets that column to its proportional default based on current container width
  const resetColWidth = useCallback((colIdx) => {
    const el = tableContainerRef.current;
    if (!el) return;
    const available = el.clientWidth - 2;
    const defaultW = Math.max(COL_MIN, Math.round((COL_RATIOS[colIdx] / COL_RATIO_SUM) * available));
    setColWidths(prev => {
      const next = [...prev];
      next[colIdx] = defaultW;
      return next;
    });
  }, []);

  const gridTemplate = colWidths.map(w => `${w}px`).join(' ');
  const totalTableWidth = colWidths.reduce((a, b) => a + b, 0);

  useEffect(() => {
    // GAAP cleanup: reset any stale progress from old auto-rollup, then load
    resetSOVProgress(projectId).catch(() => {}).finally(() => {
      fetchSOVLines();
      checkAllocation();
    });
  }, [projectId]);

  const fetchSOVLines = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getSOVLines(projectId);
      setSovLines(data);
    } catch (err) {
      setError('Failed to fetch SOV lines');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const checkAllocation = async () => {
    try {
      const status = await checkSOVAllocation(projectId);
      setAllocationStatus(status);
    } catch (err) {
      console.error('Failed to check allocation:', err);
    }
  };

  const handleAddClick = () => {
    setFormData({
      sov_code: '',
      description: '',
      building_area_phase: '',
      billing_category: 'Labor',
      contract_amount: '',
      billing_method: 'Percent Complete',
      retainage_percent: 0,
      notes: '',
    });
    setShowModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === 'contract_amount') {
      const raw = value.replace(/[^0-9.]/g, '');
      const parts = raw.split('.');
      const cleaned = parts[0] + (parts.length > 1 ? '.' + parts.slice(1).join('') : '');
      setFormData((prev) => ({
        ...prev,
        [name]: cleaned,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: name === 'retainage_percent' ? parseFloat(value) || 0 : value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      await createSOVLine({
        ...formData,
        project_id: projectId
      });
      setShowModal(false);
      await fetchSOVLines();
      await checkAllocation();
      if (refreshSummary) refreshSummary();
    } catch (err) {
      setError('Failed to create SOV line');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePercent = async (sovLineId, percentComplete) => {
    try {
      await updateSOVLine(sovLineId, { percent_complete: parseFloat(percentComplete) || 0 });
      await fetchSOVLines();
      await checkAllocation();
      if (refreshSummary) refreshSummary();
    } catch (err) {
      setError('Failed to update SOV line');
      console.error(err);
    }
  };

  const handleRollup = async (sovLineId) => {
    try {
      setLoading(true);
      await rollupSOVLine(sovLineId);
      await fetchSOVLines();
      await checkAllocation();
      if (refreshSummary) refreshSummary();
    } catch (err) {
      setError('Failed to rollup SOV line');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRollupAll = async () => {
    try {
      setLoading(true);
      for (const line of sovLines) {
        await rollupSOVLine(line.id);
      }
      await fetchSOVLines();
      await checkAllocation();
      if (refreshSummary) refreshSummary();
    } catch (err) {
      setError('Failed to rollup all SOV lines');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (sovLineId) => {
    const ok = await confirm({ title: 'Delete SOV Line', message: 'Are you sure you want to delete this SOV line? This action cannot be undone.', confirmLabel: 'Delete', danger: true });
    if (!ok) return;
    try {
      await deleteSOVLine(sovLineId);
      await fetchSOVLines();
      await checkAllocation();
      if (refreshSummary) refreshSummary();
    } catch (err) {
      setError('Failed to delete SOV line');
      console.error(err);
    }
  };

  const formatCurrency = (value) => {
    return `$${(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatCurrencyInput = (value) => {
    if (!value || value === '') return '';
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    const parts = String(value).split('.');
    const intPart = parseInt(parts[0] || '0', 10).toLocaleString('en-US');
    if (parts.length > 1) {
      return intPart + '.' + parts[1];
    }
    return intPart;
  };

  const getAllocationStatusClass = () => {
    if (!allocationStatus) return '';
    if (allocationStatus?.balanced) return 'allocation-balanced';
    if ((allocationStatus?.total_allocated || 0) > (allocationStatus?.total_contract || 0)) return 'allocation-warning';
    return 'allocation-under';
  };

  const getAllocationStatusColor = () => {
    if (!allocationStatus) return '#999';
    if (allocationStatus?.balanced) return '#4CAF50';
    if ((allocationStatus?.total_allocated || 0) > (allocationStatus?.total_contract || 0)) return '#F44336';
    return '#FFC107';
  };

  const getAllocationStatusText = () => {
    if (!allocationStatus) return 'Unknown';
    if (allocationStatus?.balanced) return 'BALANCED';
    if ((allocationStatus?.total_allocated || 0) > (allocationStatus?.total_contract || 0)) return 'OVER-ALLOCATED';
    return 'UNDER-ALLOCATED';
  };

  // Download XLSX template with Category Legend sheet
  const handleDownloadTemplate = () => {
    const wb = XLSX.utils.book_new();

    // SOV Data sheet
    const headers = ['sov_code', 'description', 'building_area_phase', 'billing_category', 'contract_amount', 'billing_method', 'retainage_percent', 'notes'];
    const sampleRows = [
      ['SOV-001', 'High School Labor', 'High School', 'Labor', 150000, 'Percent Complete', 10, 'Example line item'],
      ['SOV-002', 'High School Material', 'High School', 'Material', 200000, 'Stored Materials', 10, ''],
      ['SOV-003', 'Junior High Labor', 'Junior High', 'Labor', 100000, 'Percent Complete', 10, ''],
    ];
    const wsData = [headers, ...sampleRows];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Set column widths
    ws['!cols'] = [
      { wch: 12 }, { wch: 30 }, { wch: 20 }, { wch: 15 },
      { wch: 18 }, { wch: 22 }, { wch: 18 }, { wch: 25 }
    ];
    XLSX.utils.book_append_sheet(wb, ws, 'SOV Data');

    // Category Legend sheet
    const legendData = [
      ['FIELD', 'VALID OPTIONS / FORMAT'],
      [''],
      ['billing_category', 'Labor, Material, Equipment, Other'],
      ['billing_method', 'Percent Complete, Manual, Spread Over Duration, Milestone, Stored Materials, Not Billable'],
      ['retainage_percent', 'Number between 0 and 100 (e.g., 10)'],
      ['contract_amount', 'Number with no $ sign or commas (e.g., 150000)'],
      ['building_area_phase', 'Groups SOV lines by section (e.g., High School, Junior High, Cafeteria)'],
      ['sov_code', 'Unique code for the line item (e.g., SOV-001)'],
    ];
    const wsLegend = XLSX.utils.aoa_to_sheet(legendData);
    wsLegend['!cols'] = [{ wch: 22 }, { wch: 70 }];
    XLSX.utils.book_append_sheet(wb, wsLegend, 'Category Legend');

    XLSX.writeFile(wb, 'sov_template.xlsx');
  };

  // Parse rows from either CSV text or XLSX ArrayBuffer
  const parseFileRows = async (file) => {
    const ext = file.name.split('.').pop().toLowerCase();

    if (ext === 'xlsx' || ext === 'xls' || ext === 'xlsm') {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      // Use first sheet (SOV Data) — skip Category Legend
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: '' });
      return jsonData;
    } else {
      // CSV parsing
      const text = await file.text();
      const lines = text.split('\n').map(line => {
        const result = [];
        let current = '';
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
          const char = line[i];
          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === ',' && !inQuotes) {
            result.push(current.trim());
            current = '';
          } else {
            current += char;
          }
        }
        result.push(current.trim());
        return result;
      });

      if (lines.length < 2) return [];

      const headers = lines[0].map(h => h.toLowerCase().trim());
      const rows = [];
      for (let i = 1; i < lines.length; i++) {
        const row = lines[i];
        if (!row || row.length < 2 || !row[0] || row[0].startsWith('---') || row[0].startsWith('billing_') || row[0].startsWith('retainage_') || row[0].startsWith('contract_')) continue;
        const rowData = {};
        headers.forEach((header, idx) => {
          if (idx < row.length) rowData[header] = row[idx];
        });
        rows.push(rowData);
      }
      return rows;
    }
  };

  // Import file (CSV, XLSX, XLS, XLSM)
  const handleImportFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setLoading(true);
      setError(null);

      const rows = await parseFileRows(file);
      if (rows.length === 0) {
        setError('File must have headers and at least one data row');
        return;
      }

      let importCount = 0;
      for (const rowData of rows) {
        // Normalize header keys to lowercase
        const row = {};
        Object.keys(rowData).forEach(k => { row[k.toLowerCase().trim()] = rowData[k]; });

        if (row.description || row.sov_code) {
          await createSOVLine({
            project_id: projectId,
            sov_code: String(row.sov_code || ''),
            description: String(row.description || ''),
            building_area_phase: String(row.building_area_phase || ''),
            billing_category: String(row.billing_category || 'Labor'),
            contract_amount: parseFloat(row.contract_amount) || 0,
            billing_method: String(row.billing_method || 'Percent Complete'),
            retainage_percent: parseFloat(row.retainage_percent) || 0,
            notes: String(row.notes || ''),
          });
          importCount++;
        }
      }

      await fetchSOVLines();
      await checkAllocation();
      if (refreshSummary) refreshSummary();
      alert(`Successfully imported ${importCount} SOV line(s)`);
    } catch (err) {
      setError('Failed to import file: ' + err.message);
      console.error(err);
    } finally {
      setLoading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="sov-tab">
      {error && <div className="alert alert-error" style={{ marginBottom: '16px' }}>{error}</div>}

      {allocationStatus && (() => {
        const total = allocationStatus.total_contract || 0;
        const allocated = allocationStatus.total_allocated || 0;
        const variance = allocationStatus.variance || 0;
        const allocPct = total > 0 ? (allocated / total) * 100 : 0;
        const isBalanced = allocationStatus.balanced;
        const isOver = allocated > total;
        const sovMatchesContract = allocationStatus.sov_matches_contract;
        const projectContractValue = allocationStatus.project_contract_value || 0;

        const cardStyle = {
          flex: 1, minWidth: '180px', padding: '16px 20px',
          borderRadius: '12px', border: `1px solid ${t.cardBorder}`, backgroundColor: t.bg,
        };
        const labelStyle = {
          fontSize: '11px', fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: '0.5px', color: t.textMuted, marginBottom: '6px',
        };
        const valueStyle = { fontSize: '22px', fontWeight: 700, color: t.heading };
        const subStyle = { fontSize: '12px', color: t.textMuted, marginTop: '2px' };

        return (
          <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <div style={{
              ...cardStyle,
              borderColor: sovMatchesContract ? '#86efac' : (total > 0 && !sovMatchesContract) ? '#fde68a' : t.cardBorder,
              backgroundColor: sovMatchesContract ? '#f0fdf4' : (total > 0 && !sovMatchesContract) ? '#fffbeb' : t.bg,
            }}>
              <div style={labelStyle}>Total Scheduled Value</div>
              <div style={{ ...valueStyle, color: sovMatchesContract ? '#16a34a' : (total > 0 && !sovMatchesContract) ? '#d97706' : t.heading }}>
                {formatCurrency(total)}
              </div>
              <div style={subStyle}>
                {sovLines.length} SOV line{sovLines.length !== 1 ? 's' : ''}
                {sovMatchesContract
                  ? <span style={{ marginLeft: 8, color: '#16a34a', fontWeight: 600 }}>&#10003; Matches contract</span>
                  : total > 0 && projectContractValue > 0
                    ? <span style={{ marginLeft: 8, color: '#d97706', fontWeight: 600 }}>
                        {total > projectContractValue ? 'Over' : 'Under'} contract by {formatCurrency(Math.abs(total - projectContractValue))}
                      </span>
                    : null
                }
              </div>
            </div>
            <div style={{
              ...cardStyle,
              borderColor: isBalanced ? '#86efac' : isOver ? '#fca5a5' : '#fde68a',
              backgroundColor: isBalanced ? '#f0fdf4' : isOver ? '#fef2f2' : '#fffbeb',
            }}>
              <div style={labelStyle}>Allocated to Tasks</div>
              <div style={{ ...valueStyle, color: isBalanced ? '#16a34a' : isOver ? '#dc2626' : '#d97706' }}>
                {formatCurrency(allocated)}
              </div>
              <div style={subStyle}>{allocPct.toFixed(1)}% of contract</div>
            </div>
            <div style={cardStyle}>
              <div style={labelStyle}>{isOver ? 'Over-Allocated' : 'Unallocated'}</div>
              <div style={{ ...valueStyle, color: isOver ? '#dc2626' : variance > 0 ? '#d97706' : '#16a34a' }}>
                {formatCurrency(Math.abs(variance))}
              </div>
              <div style={subStyle}>
                {isBalanced ? 'Fully allocated' : isOver ? 'exceeds contract' : 'remaining to link'}
              </div>
            </div>
            <div style={{
              ...cardStyle, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
            }}>
              <div style={labelStyle}>Status</div>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                padding: '6px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: 700,
                backgroundColor: isBalanced ? '#dcfce7' : isOver ? '#fee2e2' : '#fef9c3',
                color: isBalanced ? '#16a34a' : isOver ? '#dc2626' : '#ca8a04',
              }}>
                <span style={{ fontSize: '16px' }}>{isBalanced ? '✓' : isOver ? '!' : '—'}</span>
                {getAllocationStatusText()}
              </div>
            </div>
          </div>
        );
      })()}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: t.heading }}>
            Schedule of Values
          </h3>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: t.textMuted }}>
            {sovView === 'billed'
              ? 'Showing finalized billing — values from last exported billing cycle.'
              : 'Showing projected — live values from current task progress.'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {/* Billed / Projected toggle */}
          <div style={{ display: 'flex', background: t.bgAlt, borderRadius: 24, padding: 3, border: `1px solid ${t.cardBorder}`, marginRight: 8 }}>
            {[
              { key: 'billed', label: 'Billed', icon: '✓' },
              { key: 'projected', label: 'Projected', icon: '◎' },
            ].map(v => (
              <button key={v.key} onClick={() => setSovView(v.key)} style={{
                padding: '6px 16px', borderRadius: 20, border: 'none', cursor: 'pointer',
                fontSize: 12, fontWeight: 600, transition: 'all .15s',
                background: sovView === v.key ? (v.key === 'billed' ? '#16a34a' : '#6366f1') : 'transparent',
                color: sovView === v.key ? '#fff' : t.textSecondary,
                boxShadow: sovView === v.key ? '0 2px 8px rgba(0,0,0,.15)' : 'none',
              }}>
                {v.icon} {v.label}
              </button>
            ))}
          </div>
          <button className="btn btn-secondary" onClick={handleDownloadTemplate} disabled={loading}
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '14px' }}>&#8681;</span> Template
          </button>
          <label className="btn btn-secondary" style={{ cursor: 'pointer', marginBottom: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '14px' }}>&#8679;</span> Import
            <input
              type="file"
              accept=".xlsx,.xls,.xlsm,.csv"
              onChange={handleImportFile}
              style={{ display: 'none' }}
            />
          </label>
          <button className="btn btn-primary" onClick={handleAddClick} disabled={loading}
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            + Add SOV Line
          </button>
        </div>
      </div>

      {showModal && (() => {
        const mInput = {
          width: '100%', padding: '10px 14px', fontSize: '14px',
          border: `1px solid ${t.inputBorder}`, borderRadius: '10px', outline: 'none',
          transition: 'border-color 0.2s', backgroundColor: t.inputBg, color: t.inputColor, boxSizing: 'border-box',
        };
        const mLabel = { display: 'block', fontSize: '14px', fontWeight: 600, color: t.heading, marginBottom: '6px' };
        const mGroup = { marginBottom: '14px' };
        return (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div onClick={(e) => e.stopPropagation()} style={{
              background: t.modalBg, borderRadius: '16px', width: '520px', maxHeight: '90vh',
              overflow: 'hidden', display: 'flex', flexDirection: 'column',
              boxShadow: '0 25px 60px rgba(0,0,0,0.3), 0 0 0 1px rgba(0,0,0,0.1)',
              animation: 'slideUp 0.25s ease',
            }}>
              {/* Header */}
              <div style={{ padding: '24px 28px 16px', borderBottom: `1px solid ${t.tableBorder}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: t.heading }}>Add SOV Line</h2>
                    <p style={{ margin: '6px 0 0', fontSize: '13px', color: t.textMuted }}>Create a new schedule of values line item.</p>
                  </div>
                  <button onClick={() => setShowModal(false)} style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
                    color: t.textMuted, fontSize: '22px', lineHeight: 1, borderRadius: '6px',
                  }}>&times;</button>
                </div>
              </div>

              {/* Body */}
              <form onSubmit={handleSubmit} style={{ flex: 1, overflowY: 'auto', padding: '20px 28px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  <div style={mGroup}>
                    <label style={mLabel}>SOV Code *</label>
                    <input type="text" name="sov_code" value={formData.sov_code} onChange={handleFormChange} required placeholder="e.g., SOV-001" style={mInput}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                  </div>
                  <div style={mGroup}>
                    <label style={mLabel}>Billing Category *</label>
                    <select name="billing_category" value={formData.billing_category} onChange={handleFormChange} style={mInput}>
                      <option>Labor</option>
                      <option>Material</option>
                      <option>Equipment</option>
                      <option>Other</option>
                    </select>
                  </div>
                </div>
                <div style={mGroup}>
                  <label style={mLabel}>Description *</label>
                  <input type="text" name="description" value={formData.description} onChange={handleFormChange} required placeholder="Description of work" style={mInput}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                </div>
                <div style={mGroup}>
                  <label style={mLabel}>Building / Area / Phase</label>
                  <input type="text" name="building_area_phase" value={formData.building_area_phase} onChange={handleFormChange} placeholder="e.g., High School" style={mInput}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  <div style={mGroup}>
                    <label style={mLabel}>Scheduled Value *</label>
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '14px', pointerEvents: 'none' }}>$</span>
                      <input type="text" name="contract_amount" value={formatCurrencyInput(formData.contract_amount)} onChange={handleFormChange} required placeholder="0.00"
                        style={{ ...mInput, paddingLeft: '28px' }}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                    </div>
                  </div>
                  <div style={mGroup}>
                    <label style={mLabel}>Retainage %</label>
                    <input type="number" name="retainage_percent" value={formData.retainage_percent} onChange={handleFormChange} placeholder="0" step="0.1" min="0" max="100" style={mInput}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                  </div>
                </div>
                <div style={mGroup}>
                  <label style={mLabel}>Billing Method *</label>
                  <select name="billing_method" value={formData.billing_method} onChange={handleFormChange} style={mInput}>
                    <option>Percent Complete</option>
                    <option>Manual</option>
                    <option>Spread Over Duration</option>
                    <option>Milestone</option>
                    <option>Stored Materials</option>
                    <option>Not Billable</option>
                  </select>
                </div>
                <div style={mGroup}>
                  <label style={mLabel}>Notes</label>
                  <textarea name="notes" value={formData.notes} onChange={handleFormChange} placeholder="Additional notes" rows="3"
                    style={{ ...mInput, resize: 'vertical', minHeight: '70px' }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                </div>

                {/* Footer */}
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', paddingTop: '8px', borderTop: `1px solid ${t.tableBorder}` }}>
                  <button type="button" onClick={() => setShowModal(false)} disabled={loading}
                    style={{
                      padding: '10px 20px', borderRadius: '10px', fontSize: '14px', fontWeight: 600,
                      border: `1px solid ${t.inputBorder}`, backgroundColor: t.bg, color: t.text, cursor: 'pointer',
                    }}>
                    Cancel
                  </button>
                  <button type="submit" disabled={loading}
                    style={{
                      padding: '10px 20px', borderRadius: '10px', fontSize: '14px', fontWeight: 600,
                      border: 'none', backgroundColor: '#3b82f6', color: '#fff', cursor: 'pointer',
                      opacity: loading ? 0.7 : 1,
                    }}>
                    {loading ? 'Creating...' : 'Create SOV Line'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        );
      })()}

      {/* SOV Lines Table */}
      <div ref={tableContainerRef} style={{
        border: `1px solid ${t.tableBorder}`, borderRadius: '12px', overflow: 'hidden',
        backgroundColor: t.tableBg, overflowX: 'auto',
      }}>
        {/* Table Header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: gridTemplate,
          padding: '14px 0',
          backgroundColor: t.tableHeaderBg,
          borderBottom: `2px solid ${t.tableBorder}`,
          fontSize: '11px', fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: '0.5px', color: t.tableHeaderColor,
          minWidth: totalTableWidth,
        }}>
          {COL_NAMES.map((name, i) => (
            <div key={i} style={{
              position: 'relative', padding: '0 10px',
              textAlign: i >= 3 && i !== 8 ? 'right' : i === 8 ? 'center' : 'left',
              overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
            }}>
              {name}
              {i < COL_NAMES.length - 1 && (
                <div
                  onMouseDown={(e) => startColResize(i, e)}
                  onDoubleClick={() => resetColWidth(i)}
                  title="Drag to resize, double-click to reset"
                  style={{
                    position: 'absolute', right: '-4px', top: 0, bottom: 0, width: '9px',
                    cursor: 'col-resize', zIndex: 5,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.08)';
                    e.currentTarget.querySelector('.resize-line').style.backgroundColor = '#3b82f6';
                  }}
                  onMouseLeave={(e) => {
                    if (resizingCol.current === null) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.querySelector('.resize-line').style.backgroundColor = '#cbd5e1';
                    }
                  }}
                >
                  <div className="resize-line" style={{
                    width: '1px', height: activeResizeCol === i ? '100%' : '60%',
                    backgroundColor: activeResizeCol === i ? '#3b82f6' : '#cbd5e1',
                    borderRadius: '1px',
                    transition: 'height 0.15s ease, background-color 0.15s ease',
                  }} />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Rows */}
        {sovLines.length === 0 ? (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>
            No SOV lines yet. Click "+ Add SOV Line" to get started.
          </div>
        ) : sovView === 'billed' && !sovLines.some(l => (parseFloat(l.last_billed_percent) || 0) > 0 || (parseFloat(l.cumulative_billed) || 0) > 0) ? (
          <div style={{ padding: '40px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 12, opacity: 0.3 }}>📋</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: t.textSecondary, marginBottom: 6 }}>No Finalized Billing Yet</div>
            <div style={{ fontSize: 13, color: t.textMuted, maxWidth: 360, margin: '0 auto', lineHeight: 1.6 }}>
              Billed values appear here after a billing period is exported. Switch to the <span style={{ color: '#6366f1', fontWeight: 600, cursor: 'pointer' }} onClick={() => setSovView('projected')}>Projected</span> view to see live progress from the schedule.
            </div>
          </div>
        ) : (
          sovLines
            .slice()
            .sort((a, b) => {
              const aCode = a.sov_code ? parseInt(a.sov_code.replace(/\D/g, '')) || 0 : 0;
              const bCode = b.sov_code ? parseInt(b.sov_code.replace(/\D/g, '')) || 0 : 0;
              return aCode - bCode;
            })
            .map((line, idx) => {
              const contractAmt = parseFloat(line.contract_amount) || 0;
              const retainagePct = parseFloat(line.retainage_percent) || 0;
              const retainageReleased = parseFloat(line.retainage_released) || 0;
              const storedMat = parseFloat(line.stored_materials_amount) || 0;

              // Billed view: from last finalized billing export
              // Projected view: live from current task progress
              const isBilled = sovView === 'billed';
              const pct = isBilled
                ? (parseFloat(line.last_billed_percent) || 0)
                : (parseFloat(line.percent_complete) || 0);
              const earnedAmt = isBilled
                ? (parseFloat(line.last_billed_earned) || 0)
                : (parseFloat(line.earned_amount) || 0);
              // Billed view: "Previous" = what was billed before the last finalized period
              // Projected view: "Previous" = everything already officially billed (cumulative)
              const prevBilled = isBilled
                ? (parseFloat(line.previous_billed_amount) || 0)
                : (parseFloat(line.cumulative_billed) || 0);
              // "This Period" = gross earned delta beyond what's already been billed
              const curBilled = Math.max(0, earnedAmt - prevBilled);
              const total = earnedAmt + storedMat;
              const balance = contractAmt - total;
              const retainageAmt = (total * retainagePct) / 100;
              const billedDate = line.last_billed_date;
              const hasBilledData = (parseFloat(line.last_billed_percent) || 0) > 0 || (parseFloat(line.cumulative_billed) || 0) > 0;

              return (
                <div key={line.id} style={{
                  display: 'grid',
                  gridTemplateColumns: gridTemplate,
                  padding: '14px 0',
                  borderBottom: idx < sovLines.length - 1 ? `1px solid ${t.tableBorder}` : 'none',
                  alignItems: 'center',
                  transition: 'background-color 0.1s',
                  minWidth: totalTableWidth,
                }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = t.tableRowHover}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  {/* Item No. — double-click to open properties */}
                  <div
                    onDoubleClick={() => openPropsModal(line)}
                    style={{ fontSize: '13px', fontWeight: 600, color: '#3b82f6', padding: '0 10px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}
                    title="Double-click to edit properties"
                  >
                    {line.sov_code}
                  </div>

                  {/* Description */}
                  <div style={{ padding: '0 10px', overflow: 'hidden' }}>
                    <div style={{ fontSize: '13px', fontWeight: 500, color: t.heading, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{line.description}</div>
                    {line.building_area_phase && (
                      <div style={{ fontSize: '11px', color: t.textMuted, marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{line.building_area_phase}</div>
                    )}
                  </div>

                  {/* Category */}
                  <div style={{ padding: '0 10px', overflow: 'hidden' }}>
                    <span style={{
                      ...getCategoryStyle(line.billing_category),
                      padding: '3px 10px', borderRadius: '10px',
                      fontSize: '11px', fontWeight: 600,
                      display: 'inline-block', whiteSpace: 'nowrap',
                    }}>{line.billing_category}</span>
                  </div>

                  {/* Scheduled Value */}
                  <div style={{ textAlign: 'right', fontSize: '13px', fontWeight: 500, color: t.text, padding: '0 10px' }}>
                    {formatCurrency(contractAmt)}
                  </div>

                  {/* Previous */}
                  <div style={{ textAlign: 'right', fontSize: '13px', fontWeight: 500, color: t.textSecondary, padding: '0 10px' }}>
                    {formatCurrency(prevBilled)}
                  </div>

                  {/* This Period */}
                  <div style={{ textAlign: 'right', fontSize: '13px', fontWeight: 500, padding: '0 10px',
                    color: isBilled ? t.text : '#6366f1',
                    fontStyle: isBilled ? 'normal' : 'italic',
                  }}>
                    {formatCurrency(curBilled)}
                    {!isBilled && curBilled > 0 && <span style={{ fontSize: 9, marginLeft: 3, color: t.textMuted, fontStyle: 'normal' }}>est</span>}
                  </div>

                  {/* Stored Materials */}
                  <div style={{ textAlign: 'right', fontSize: '13px', fontWeight: 500, color: t.text, padding: '0 10px' }}>
                    {formatCurrency(storedMat)}
                  </div>

                  {/* Total */}
                  <div style={{ textAlign: 'right', fontSize: '13px', fontWeight: 600, color: t.heading, padding: '0 10px' }}>
                    {formatCurrency(total)}
                  </div>

                  {/* % */}
                  <div style={{ textAlign: 'center', fontSize: '13px', fontWeight: 500, padding: '0 10px',
                    color: isBilled ? t.text : '#6366f1',
                    fontStyle: isBilled ? 'normal' : 'italic',
                  }}>
                    {contractAmt > 0 ? Math.round(pct) : 0}
                    {!isBilled && <span style={{ fontSize: 9, color: t.textMuted, fontStyle: 'normal' }}>%</span>}
                    {isBilled && '%'}
                  </div>

                  {/* Balance */}
                  <div style={{ textAlign: 'right', fontSize: '13px', fontWeight: balance === 0 && contractAmt > 0 ? 600 : 500, color: balance === 0 && contractAmt > 0 ? '#16a34a' : balance > 0 ? t.text : '#dc2626', padding: '0 10px' }}>
                    {formatCurrency(balance)}
                  </div>

                  {/* Retainage */}
                  <div style={{ textAlign: 'right', fontSize: '13px', fontWeight: 500, color: '#d97706', padding: '0 10px' }}>
                    {formatCurrency(retainageAmt)}
                  </div>

                  {/* Released */}
                  <div style={{ textAlign: 'right', fontSize: '12px', padding: '0 6px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                    {retainageReleased > 0 ? (
                      <span style={{ color: '#16a34a', fontWeight: 600, fontSize: 12 }}>{formatCurrency(retainageReleased)}</span>
                    ) : null}
                    {retainageAmt > 0 && retainageReleased === 0 && (
                      <span style={{ color: t.textSecondary, fontSize: 10 }}>–</span>
                    )}
                    {retainageAmt > 0 && retainageAmt <= retainageReleased && (
                      <span style={{ color: '#16a34a', fontSize: 10, fontWeight: 600 }}>Full</span>
                    )}
                  </div>

                  {/* Delete */}
                  <div style={{ textAlign: 'center', padding: '0 4px' }}>
                    <button
                      onClick={() => handleDelete(line.id)}
                      disabled={loading}
                      title="Delete this SOV line"
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
                        color: '#cbd5e1', fontSize: '16px', lineHeight: 1, borderRadius: '6px',
                        transition: 'all 0.15s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.backgroundColor = '#fef2f2'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = '#cbd5e1'; e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  </div>
                </div>
              );
            })
        )}

        {/* Totals Row */}
        {sovLines.length > 0 && (() => {
          const isBilled = sovView === 'billed';
          const totalContract = sovLines.reduce((s, l) => s + (parseFloat(l.contract_amount) || 0), 0);
          const totalPrevBilled = isBilled
            ? sovLines.reduce((s, l) => s + (parseFloat(l.previous_billed_amount) || 0), 0)
            : sovLines.reduce((s, l) => s + (parseFloat(l.cumulative_billed) || 0), 0);
          const totalStoredMat = sovLines.reduce((s, l) => s + (parseFloat(l.stored_materials_amount) || 0), 0);

          // "This Period" always shows gross earned delta, not net after retainage
          const totalEarned = isBilled
            ? sovLines.reduce((s, l) => s + (parseFloat(l.last_billed_earned) || 0), 0)
            : sovLines.reduce((s, l) => s + (parseFloat(l.earned_amount) || 0), 0);
          const totalCurBilled = Math.max(0, totalEarned - totalPrevBilled);
          const grandTotal = totalEarned + totalStoredMat;

          const totalBalance = totalContract - grandTotal;
          const totalRetainage = sovLines.reduce((s, l) => {
            const amt = parseFloat(l.contract_amount) || 0;
            const pctVal = isBilled ? (parseFloat(l.last_billed_percent) || 0) : (parseFloat(l.percent_complete) || 0);
            const earned = amt * pctVal / 100;
            const stored = parseFloat(l.stored_materials_amount) || 0;
            const retPct = parseFloat(l.retainage_percent) || 0;
            return s + ((earned + stored) * retPct / 100);
          }, 0);
          const totalReleased = sovLines.reduce((s, l) => s + (parseFloat(l.retainage_released) || 0), 0);
          const overallPct = totalContract > 0 ? Math.round((grandTotal / totalContract) * 100) : 0;
          return (
            <div style={{
              display: 'grid',
              gridTemplateColumns: gridTemplate,
              padding: '14px 0',
              borderTop: `2px solid ${t.tableBorder}`,
              backgroundColor: t.totalsBg,
              fontSize: '13px', fontWeight: 700, color: t.heading,
              alignItems: 'center',
              minWidth: totalTableWidth,
            }}>
              <div style={{ padding: '0 10px' }}></div>
              <div style={{ padding: '0 10px' }}>Totals ({sovLines.length} lines){!isBilled && <span style={{ fontSize: 10, fontWeight: 500, color: '#6366f1', marginLeft: 6, fontStyle: 'italic' }}>projected</span>}</div>
              <div style={{ padding: '0 10px' }}></div>
              <div style={{ textAlign: 'right', padding: '0 10px' }}>{formatCurrency(totalContract)}</div>
              <div style={{ textAlign: 'right', padding: '0 10px' }}>{formatCurrency(totalPrevBilled)}</div>
              <div style={{ textAlign: 'right', padding: '0 10px', color: isBilled ? t.heading : '#6366f1', fontStyle: isBilled ? 'normal' : 'italic' }}>{formatCurrency(totalCurBilled)}</div>
              <div style={{ textAlign: 'right', padding: '0 10px' }}>{formatCurrency(totalStoredMat)}</div>
              <div style={{ textAlign: 'right', padding: '0 10px' }}>{formatCurrency(grandTotal)}</div>
              <div style={{ textAlign: 'center', padding: '0 10px' }}>{overallPct}%</div>
              <div style={{ textAlign: 'right', padding: '0 10px' }}>{formatCurrency(totalBalance)}</div>
              <div style={{ textAlign: 'right', padding: '0 10px', color: '#d97706' }}>{formatCurrency(totalRetainage)}</div>
              <div style={{ textAlign: 'right', padding: '0 10px', color: totalReleased > 0 ? '#16a34a' : undefined }}>{totalReleased > 0 ? formatCurrency(totalReleased) : '–'}</div>
              <div style={{ padding: '0 10px' }}></div>
            </div>
          );
        })()}
      </div>
      <ConfirmDialog {...confirmDialogProps} />

      {/* ═══ SOV LINE PROPERTIES MODAL ═══ */}
      {propsModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={(e) => { if (e.target === e.currentTarget) setPropsModal(null); }}
        >
          <div style={{ backgroundColor: t.bg, borderRadius: 16, width: 620, maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,.2)' }}>
            {/* Header */}
            <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${t.cardBorder}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: t.heading }}>{propsModal.sov_code} Properties</h2>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: t.textSecondary }}>{propsModal.description}</p>
              </div>
              <button onClick={() => setPropsModal(null)} style={{ background: 'none', border: 'none', fontSize: 22, color: t.textSecondary, cursor: 'pointer', lineHeight: 1 }}>&times;</button>
            </div>

            {/* Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
              {/* Editable fields */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: t.textSecondary, marginBottom: 4 }}>SOV Code</label>
                  <input value={propsForm.sov_code} onChange={e => setPropsForm(f => ({ ...f, sov_code: e.target.value }))}
                    style={{ width: '100%', padding: '8px 12px', fontSize: 13, border: `1px solid ${t.cardBorder}`, borderRadius: 8, backgroundColor: t.bg, color: t.text, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: t.textSecondary, marginBottom: 4 }}>Category</label>
                  <select value={propsForm.billing_category} onChange={e => setPropsForm(f => ({ ...f, billing_category: e.target.value }))}
                    style={{ width: '100%', padding: '8px 12px', fontSize: 13, border: `1px solid ${t.cardBorder}`, borderRadius: 8, backgroundColor: t.bg, color: t.text, boxSizing: 'border-box' }}>
                    <option>Labor</option><option>Material</option><option>General Conditions</option><option>Equipment</option><option>Other</option>
                  </select>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: t.textSecondary, marginBottom: 4 }}>Description</label>
                  <input value={propsForm.description} onChange={e => setPropsForm(f => ({ ...f, description: e.target.value }))}
                    style={{ width: '100%', padding: '8px 12px', fontSize: 13, border: `1px solid ${t.cardBorder}`, borderRadius: 8, backgroundColor: t.bg, color: t.text, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: t.textSecondary, marginBottom: 4 }}>Building Area / Phase</label>
                  <input value={propsForm.building_area_phase} onChange={e => setPropsForm(f => ({ ...f, building_area_phase: e.target.value }))}
                    style={{ width: '100%', padding: '8px 12px', fontSize: 13, border: `1px solid ${t.cardBorder}`, borderRadius: 8, backgroundColor: t.bg, color: t.text, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: t.textSecondary, marginBottom: 4 }}>Contract Amount</label>
                  <input type="number" step="0.01" value={propsForm.contract_amount} onChange={e => setPropsForm(f => ({ ...f, contract_amount: parseFloat(e.target.value) || 0 }))}
                    style={{ width: '100%', padding: '8px 12px', fontSize: 13, border: `1px solid ${t.cardBorder}`, borderRadius: 8, backgroundColor: t.bg, color: t.text, boxSizing: 'border-box' }} />
                </div>
              </div>

              {/* Retainage section */}
              <div style={{ padding: 16, border: `1px solid ${t.cardBorder}`, borderRadius: 12, marginBottom: 20, backgroundColor: t.bgAlt }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: t.heading, marginBottom: 12 }}>Retention Settings</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: t.textSecondary, marginBottom: 4 }}>Retention for Work (%)</label>
                    <input type="number" step="0.1" min="0" max="100" value={propsForm.retainage_percent}
                      onChange={e => setPropsForm(f => ({ ...f, retainage_percent: parseFloat(e.target.value) || 0 }))}
                      style={{ width: '100%', padding: '8px 12px', fontSize: 13, border: `1px solid ${t.cardBorder}`, borderRadius: 8, backgroundColor: t.bg, color: t.text, boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: t.textSecondary, marginBottom: 4 }}>Retention for Stored Material (%)</label>
                    <input type="number" step="0.1" min="0" max="100" value={propsForm.retainage_percent_stored}
                      onChange={e => setPropsForm(f => ({ ...f, retainage_percent_stored: parseFloat(e.target.value) || 0 }))}
                      style={{ width: '100%', padding: '8px 12px', fontSize: 13, border: `1px solid ${t.cardBorder}`, borderRadius: 8, backgroundColor: t.bg, color: t.text, boxSizing: 'border-box' }} />
                  </div>
                </div>
              </div>

              {/* Notes & Change Reason */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: t.textSecondary, marginBottom: 4 }}>Notes</label>
                <textarea value={propsForm.notes} onChange={e => setPropsForm(f => ({ ...f, notes: e.target.value }))} rows={2}
                  style={{ width: '100%', padding: '8px 12px', fontSize: 13, border: `1px solid ${t.cardBorder}`, borderRadius: 8, backgroundColor: t.bg, color: t.text, boxSizing: 'border-box', resize: 'vertical' }} />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#d97706', marginBottom: 4 }}>Reason for Change (optional)</label>
                <input value={propsChangeReason} onChange={e => setPropsChangeReason(e.target.value)} placeholder="e.g., Change order #3, Owner request..."
                  style={{ width: '100%', padding: '8px 12px', fontSize: 13, border: '1px solid #fbbf24', borderRadius: 8, backgroundColor: '#fffbeb', color: t.text, boxSizing: 'border-box' }} />
              </div>

            </div>

            {/* Footer */}
            <div style={{ padding: '16px 24px', borderTop: `1px solid ${t.cardBorder}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setPropsModal(null)} style={{ padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: `1px solid ${t.cardBorder}`, backgroundColor: t.bg, color: t.text, cursor: 'pointer' }}>Cancel</button>
              <button onClick={handlePropsSave} disabled={propsSaving}
                style={{ padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: 'none', backgroundColor: '#3b82f6', color: '#fff', cursor: propsSaving ? 'wait' : 'pointer', opacity: propsSaving ? 0.6 : 1 }}>
                {propsSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SOVTab;
